import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_URL from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { 
  FiCheckCircle, 
  FiClock, 
  FiAlertCircle,
  FiFlag,
  FiUser,
  FiCalendar,
  FiPlus,
  FiFilter,
  FiChevronDown,
  FiChevronUp,
  FiMessageSquare,
  FiPaperclip,
  FiEye
} from 'react-icons/fi';
import { formatDistanceToNow, format } from 'date-fns';
import { es } from 'date-fns/locale';
import toast from 'react-hot-toast';

const Tasks = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskDetails, setShowTaskDetails] = useState(false);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    assignedTo: '',
    page: 1
  });
  const [pagination, setPagination] = useState({
    total: 0,
    pages: 1,
    current: 1
  });
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    assignedTo: '',
    priority: 'normal',
    dueDate: ''
  });

  useEffect(() => {
    fetchTasks();
    fetchUsers();
  }, [filters.status, filters.priority, filters.assignedTo, filters.page]);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: filters.page,
        limit: 10,
        ...(filters.status && { status: filters.status }),
        ...(filters.priority && { priority: filters.priority }),
        ...(filters.assignedTo && { assignedTo: filters.assignedTo })
      });

      const response = await axios.get(`${API_URL}/api/tasks?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      setTasks(response.data.data || []);
      setPagination({
        total: response.data.total || 0,
        pages: response.data.pages || 1,
        current: response.data.page || 1
      });
    } catch (error) {
      console.error('Error fetching tasks:', error);
      toast.error('Error al cargar tareas');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
  try {
    const token = localStorage.getItem('token');
    // Usar el endpoint específico para asignaciones
    const response = await axios.get(`${API_URL}/api/users/assignable`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setUsers(response.data.data || []);
  } catch (error) {
    console.error('Error fetching assignable users:', error);
    toast.error('Error al cargar usuarios para asignar');
  }
};

  const handleCreateTask = async (e) => {
    e.preventDefault();
    
    try {
      const response = await axios.post(
        `${API_URL}/api/tasks`,
        formData,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      toast.success('Tarea creada exitosamente');
      setShowCreateModal(false);
      resetForm();
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al crear tarea');
    }
  };

  const handleUpdateStatus = async (taskId, newStatus) => {
    try {
      await axios.put(
        `${API_URL}/api/tasks/${taskId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      toast.success(`Tarea ${newStatus === 'completada' ? 'completada' : 'actualizada'}`);
      fetchTasks();
    } catch (error) {
      toast.error('Error al actualizar tarea');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      assignedTo: '',
      priority: 'normal',
      dueDate: ''
    });
  };

  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'urgente': return 'bg-red-100 text-red-800 border-red-200';
      case 'alta': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'normal': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'baja': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityIcon = (priority) => {
    switch(priority) {
      case 'urgente': return <FiAlertCircle className="text-red-600" size={16} />;
      case 'alta': return <FiFlag className="text-orange-600" size={16} />;
      case 'normal': return <FiFlag className="text-blue-600" size={16} />;
      case 'baja': return <FiFlag className="text-green-600" size={16} />;
      default: return null;
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'completada': return 'bg-green-100 text-green-800';
      case 'en_progreso': return 'bg-yellow-100 text-yellow-800';
      case 'pendiente': return 'bg-gray-100 text-gray-800';
      case 'cancelada': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getDueDateStatus = (dueDate) => {
    const today = new Date();
    const due = new Date(dueDate);
    const diffDays = Math.ceil((due - today) / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return { color: 'text-red-600', text: `Vencida hace ${Math.abs(diffDays)} días` };
    if (diffDays === 0) return { color: 'text-red-600', text: 'Vence hoy' };
    if (diffDays <= 3) return { color: 'text-yellow-600', text: `Vence en ${diffDays} días` };
    return { color: 'text-green-600', text: `Vence en ${diffDays} días` };
  };

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
            Tareas y Coordinación
          </h1>
          <p className="text-sm md:text-base text-gray-600 mt-1">
            Gestiona las tareas del equipo
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all"
        >
          <FiPlus className="mr-2" size={18} />
          Nueva Tarea
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800"
        >
          <FiFilter size={18} />
          Filtros
          {showFilters ? <FiChevronUp size={18} /> : <FiChevronDown size={18} />}
        </button>

        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Estado
              </label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({...filters, status: e.target.value, page: 1})}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value="">Todos</option>
                <option value="pendiente">Pendiente</option>
                <option value="en_progreso">En progreso</option>
                <option value="completada">Completada</option>
                <option value="cancelada">Cancelada</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Prioridad
              </label>
              <select
                value={filters.priority}
                onChange={(e) => setFilters({...filters, priority: e.target.value, page: 1})}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value="">Todas</option>
                <option value="urgente">Urgente</option>
                <option value="alta">Alta</option>
                <option value="normal">Normal</option>
                <option value="baja">Baja</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Asignado a
              </label>
              <select
                value={filters.assignedTo}
                onChange={(e) => setFilters({...filters, assignedTo: e.target.value, page: 1})}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value="">Todos</option>
                {users.map(u => (
                  <option key={u._id} value={u._id}>
                    {u.name} {u.role === 'owner' ? '(Owner)' : `(Admin ${u.adminId})`}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Tasks List */}
      <div className="space-y-4">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
          </div>
        ) : tasks.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <FiCheckCircle className="mx-auto text-4xl text-gray-400 mb-3" />
            <p className="text-gray-500">No hay tareas para mostrar</p>
          </div>
        ) : (
          tasks.map(task => {
            const dueDateStatus = getDueDateStatus(task.dueDate);
            return (
              <div
                key={task._id}
                className="bg-white rounded-lg shadow p-4 hover:shadow-md transition-all border-l-4"
                style={{
                  borderLeftColor: 
                    task.priority === 'urgente' ? '#EF4444' :
                    task.priority === 'alta' ? '#F97316' :
                    task.priority === 'normal' ? '#3B82F6' : '#10B981'
                }}
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-lg">{task.title}</h3>
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(task.status)}`}>
                        {task.status === 'en_progreso' ? 'En progreso' : task.status}
                      </span>
                      <span className={`px-2 py-1 text-xs rounded-full ${getPriorityColor(task.priority)}`}>
                        {task.priority}
                      </span>
                    </div>
                    
                    <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                      {task.description}
                    </p>
                    
                    <div className="flex flex-wrap items-center gap-4 text-sm">
                      <div className="flex items-center gap-1 text-gray-600">
                        <FiUser size={14} />
                        <span>
                          {task.assignedTo?.name}
                          {task.assignedTo?.role === 'owner' ? ' (Owner)' : ` (Admin ${task.assignedTo?.adminId})`}
                        </span>
                      </div>
                      
                      <div className={`flex items-center gap-1 ${dueDateStatus.color}`}>
                        <FiCalendar size={14} />
                        <span>{dueDateStatus.text}</span>
                      </div>
                      
                      {task.comments?.length > 0 && (
                        <div className="flex items-center gap-1 text-gray-600">
                          <FiMessageSquare size={14} />
                          <span>{task.comments.length} comentarios</span>
                        </div>
                      )}
                      
                      {task.attachments?.length > 0 && (
                        <div className="flex items-center gap-1 text-gray-600">
                          <FiPaperclip size={14} />
                          <span>{task.attachments.length} archivos</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 md:flex-col md:items-end">
                    <button
                      onClick={() => {
                        setSelectedTask(task);
                        setShowTaskDetails(true);
                      }}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                      title="Ver detalles"
                    >
                      <FiEye size={18} />
                    </button>
                    
                    {task.status !== 'completada' && task.status !== 'cancelada' && (
                      <>
                        {task.assignedTo?._id === user?._id && (
                          <button
                            onClick={() => handleUpdateStatus(task._id, 'en_progreso')}
                            className="p-2 text-yellow-600 hover:bg-yellow-50 rounded-lg"
                            title="Marcar en progreso"
                          >
                            <FiClock size={18} />
                          </button>
                        )}
                        <button
                          onClick={() => handleUpdateStatus(task._id, 'completada')}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                          title="Marcar completada"
                        >
                          <FiCheckCircle size={18} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="mt-6 flex justify-center gap-2">
          <button
            onClick={() => setFilters({...filters, page: filters.page - 1})}
            disabled={filters.page === 1}
            className="px-4 py-2 border rounded-lg disabled:opacity-50 hover:bg-gray-50"
          >
            Anterior
          </button>
          <span className="px-4 py-2 bg-blue-50 text-blue-700 rounded-lg">
            Página {filters.page} de {pagination.pages}
          </span>
          <button
            onClick={() => setFilters({...filters, page: filters.page + 1})}
            disabled={filters.page === pagination.pages}
            className="px-4 py-2 border rounded-lg disabled:opacity-50 hover:bg-gray-50"
          >
            Siguiente
          </button>
        </div>
      )}

      {/* Create Task Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold mb-4">Crear Nueva Tarea</h2>
            
            <form onSubmit={handleCreateTask} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Título *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descripción *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows="3"
                  className="w-full border rounded-lg px-3 py-2"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Asignar a *
                </label>
                <select
                  value={formData.assignedTo}
                  onChange={(e) => setFormData({...formData, assignedTo: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2"
                  required
                >
                  <option value="">Seleccionar</option>
                  {users.map(u => (
                    <option key={u._id} value={u._id}>
                      {u.name} {u.role === 'owner' ? '(Owner)' : `(Admin ${u.adminId})`}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Prioridad
                </label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData({...formData, priority: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="baja">Baja</option>
                  <option value="normal">Normal</option>
                  <option value="alta">Alta</option>
                  <option value="urgente">Urgente</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fecha límite *
                </label>
                <input
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) => setFormData({...formData, dueDate: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2"
                  required
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Crear Tarea
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tasks;